/**
 * Educational image hotspots for clothing-guide pages.
 * Desktop: hover previews content; mouse leave restores last clicked / default first.
 * Touch: tap selects content.
 */
class GuideHotspots extends HTMLElement {
  connectedCallback() {
    this.dots = Array.from(this.querySelectorAll('[data-guide-hotspot-dot]'));
    this.panels = Array.from(this.querySelectorAll('[data-guide-hotspot-panel]'));
    this.activeId = null;
    this.selectedId = this.dots[0]?.dataset.guideHotspotDot || null;
    this.closeTimer = null;
    this.hoverEnabled = window.matchMedia('(hover: hover) and (pointer: fine)').matches;

    this.onDotClick = this.onDotClick.bind(this);
    this.onDotEnter = this.onDotEnter.bind(this);
    this.onDotLeave = this.onDotLeave.bind(this);
    this.onPanelEnter = this.onPanelEnter.bind(this);
    this.onPanelLeave = this.onPanelLeave.bind(this);
    this.onKeydown = this.onKeydown.bind(this);

    this.dots.forEach((dot) => {
      dot.addEventListener('click', this.onDotClick);
      if (this.hoverEnabled) {
        dot.addEventListener('mouseenter', this.onDotEnter);
        dot.addEventListener('mouseleave', this.onDotLeave);
        dot.addEventListener('focus', this.onDotEnter);
        dot.addEventListener('blur', this.onDotLeave);
      }
    });

    this.panels.forEach((panel) => {
      if (this.hoverEnabled) {
        panel.addEventListener('mouseenter', this.onPanelEnter);
        panel.addEventListener('mouseleave', this.onPanelLeave);
      }
    });

    document.addEventListener('keydown', this.onKeydown);

    if (this.selectedId) {
      this.open(this.selectedId);
    }
  }

  disconnectedCallback() {
    this.clearCloseTimer();
    this.dots.forEach((dot) => {
      dot.removeEventListener('click', this.onDotClick);
      dot.removeEventListener('mouseenter', this.onDotEnter);
      dot.removeEventListener('mouseleave', this.onDotLeave);
      dot.removeEventListener('focus', this.onDotEnter);
      dot.removeEventListener('blur', this.onDotLeave);
    });
    this.panels.forEach((panel) => {
      panel.removeEventListener('mouseenter', this.onPanelEnter);
      panel.removeEventListener('mouseleave', this.onPanelLeave);
    });
    document.removeEventListener('keydown', this.onKeydown);
  }

  clearCloseTimer() {
    if (this.closeTimer) {
      clearTimeout(this.closeTimer);
      this.closeTimer = null;
    }
  }

  restoreSelected() {
    this.clearCloseTimer();
    this.closeTimer = setTimeout(() => {
      if (this.selectedId) this.open(this.selectedId);
    }, 120);
  }

  onDotClick(event) {
    event.preventDefault();
    event.stopPropagation();
    const id = event.currentTarget.dataset.guideHotspotDot;
    this.selectedId = id;
    this.clearCloseTimer();
    this.open(id);
  }

  onDotEnter(event) {
    this.clearCloseTimer();
    this.open(event.currentTarget.dataset.guideHotspotDot);
  }

  onDotLeave() {
    this.restoreSelected();
  }

  onPanelEnter() {
    this.clearCloseTimer();
  }

  onPanelLeave() {
    this.restoreSelected();
  }

  onKeydown(event) {
    if (event.key === 'Escape' && this.selectedId) {
      this.open(this.selectedId);
    }
  }

  open(id) {
    this.activeId = id;
    this.dots.forEach((dot) => {
      const isActive = dot.dataset.guideHotspotDot === id;
      const isSelected = dot.dataset.guideHotspotDot === this.selectedId;
      dot.classList.toggle('is-active', isActive);
      dot.classList.toggle('is-selected', isSelected);
      dot.setAttribute('aria-expanded', isActive ? 'true' : 'false');
    });
    this.panels.forEach((panel) => {
      const isActive = panel.dataset.guideHotspotPanel === id;
      panel.hidden = !isActive;
      panel.classList.toggle('is-active', isActive);
    });
  }
}

if (!customElements.get('guide-hotspots')) {
  customElements.define('guide-hotspots', GuideHotspots);
}
